export type CmtsIsoDateString = string;

export type ComplaintCreateFormValues = Readonly<{
  title?: string | undefined;
  description?: string | undefined;
  sourceId?: string | undefined;
  complaintTypeId?: string | undefined;
  issueTypeId?: string | undefined;
  typeId?: string | undefined;
  categoryId?: string | undefined;
  subCategoryId?: string | undefined;
  subcategoryId?: string | undefined;
  severityId?: string | undefined;
  priorityId?: string | undefined;
  organizationUnitId?: string | undefined;
  reportedAt?: CmtsIsoDateString | undefined;
  customerReference?: string | undefined;
}>;

export type ComplaintCreateContext = Readonly<{
  organizationUnitId?: string | undefined;
  organizationUnitIds?: readonly string[] | undefined;
}>;

export type ComplaintCreatePayload = Readonly<{
  sourceId: string;
  complaintTypeId?: string | undefined;
  categoryId: string;
  subCategoryId?: string | undefined;
  severityId: string;
  priorityId: string;
  organizationUnitId: string;
  title: string;
  description: string;
  reportedAt?: CmtsIsoDateString | undefined;
  plantId?: string | undefined;
  departmentId?: string | undefined;
  externalPartyId?: string | undefined;
  partNumber?: string | undefined;
  batchNumber?: string | undefined;
  lotNumber?: string | undefined;
  tagIds?: readonly string[] | undefined;
  fieldValues?: Readonly<Record<string, string>> | undefined;
  isConfidential?: boolean | undefined;
  isAnonymous?: boolean | undefined;
  customerId?: string | undefined;
  supplierId?: string | undefined;
  internalPartyId?: string | undefined;
  productId?: string | undefined;
  processId?: string | undefined;
  productionLineId?: string | undefined;
  machineId?: string | undefined;
  ownerUserId?: string | undefined;
  functionalExpertId?: string | undefined;
  occurrenceAt?: CmtsIsoDateString | undefined;
  detectionAt?: CmtsIsoDateString | undefined;
  reporterName?: string | undefined;
  reporterEmail?: string | undefined;
  externalReferenceNumber?: string | undefined;
  serialNumber?: string | undefined;
  quantityAffected?: number | undefined;
  confidentialityLevelId?: string | undefined;
  idempotencyKey?: string | undefined;
  clientMutationId?: string | undefined;
  correlationId?: string | undefined;
}>;

export type ComplaintUpdatePayload = Partial<ComplaintCreatePayload> & Readonly<{ rowVersion?: string | undefined }>;

export type WorkflowActionPayload = Readonly<{
  actionCode?: string | undefined;
  remarks?: string | undefined;
  reason?: string | undefined;
  comment?: string | undefined;
}>;

export type RcaCreatePayload = Readonly<{
  complaintId: string;
  rcaMethodId: string;
  rootCauseCategoryId: string;
  problemStatement: string;
  investigationSummary?: string | undefined;
  rootCause?: string | undefined;
  ownerUserId: string;
  targetDate: CmtsIsoDateString;
  statusId: string;
}>;

export type RcaActionPayload = Readonly<{
  remarks?: string | undefined;
  reason?: string | undefined;
  rootCause?: string | undefined;
  investigationSummary?: string | undefined;
  completedStatusId?: string | undefined;
  rowVersion?: string | undefined;
}>;

export type CapaCreatePayload = Readonly<{
  complaintId: string;
  actionTypeId: string;
  actionDescription: string;
  ownerUserId: string;
  dueDate: CmtsIsoDateString;
  statusId: string;
  effectivenessRequired: boolean;
  effectivenessCheckDate?: CmtsIsoDateString | undefined;
}>;

export type CapaActionPayload = Readonly<{
  remarks?: string | undefined;
  reason?: string | undefined;
  assignedToUserId?: string | undefined;
  closedStatusId?: string | undefined;
  effectivenessStatusId?: string | undefined;
  closureComment?: string | undefined;
  rowVersion?: string | undefined;
}>;

export type VerificationCreatePayload = Readonly<{
  complaintId: string;
  verificationRuleId: string;
  verifierUserId: string;
  pendingStatusId: string;
}>;

export type VerificationDecisionPayload = Readonly<{
  verificationStatusId: string;
  remarks?: string | undefined;
  rowVersion?: string | undefined;
}>;


export type EvidenceConfidentiality = string;

export type EvidenceUploadMetadata = Readonly<{
  description?: string | undefined;
  confidentiality: EvidenceConfidentiality;
  fileName: string;
  fileType: string;
  sizeBytes: number;
}>;

function optionalTrimmed(value: unknown): string | undefined {
  const text = typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value).trim() : "";
  return text ? text : undefined;
}

function requiredTrimmed(values: Readonly<Record<string, unknown>>, field: string): string {
  const value = optionalTrimmed(values[field]);
  if (!value) throw new Error(`${field} is required.`);
  return value;
}

function minLength(value: string, field: string, minimum: number): string {
  const trimmed = value.trim();
  if (trimmed.length < minimum) throw new Error(`${field} must contain at least ${minimum} characters.`);
  return trimmed;
}

function maxLength(value: string, field: string, maximum: number): string {
  const trimmed = value.trim();
  if (trimmed.length > maximum) throw new Error(`${field} must contain no more than ${maximum} characters.`);
  return trimmed;
}

function optionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(normalized)) return true;
    if (["false", "no", "0"].includes(normalized)) return false;
  }
  return undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function optionalStringArray(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((item) => optionalTrimmed(item)).filter((item): item is string => Boolean(item));
  return items.length ? items : undefined;
}

function optionalStringRecord(value: unknown): Readonly<Record<string, string>> | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const output: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    const cleanKey = optionalTrimmed(key);
    const cleanValue = optionalTrimmed(raw);
    if (cleanKey && cleanValue) output[cleanKey] = cleanValue;
  }
  return Object.keys(output).length ? output : undefined;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function optionalDate(value: unknown): CmtsIsoDateString | undefined {
  const trimmed = optionalTrimmed(value);
  if (!trimmed) return undefined;
  // HTML <input type="date"> gives "YYYY-MM-DD" with no time component.
  // Backend DateTimeOffset? fields require a full ISO-8601 date-time string
  // (System.Text.Json rejects date-only strings for DateTimeOffset), so we
  // append midnight UTC here to keep the value a valid calendar date on any server timezone.
  if (DATE_ONLY_PATTERN.test(trimmed)) return `${trimmed}T00:00:00.000Z`;
  return trimmed;
}

export function requireCmtsIdentifier(value: unknown, field: string): string {
  return requiredTrimmed({ [field]: value }, field);
}

function firstTrimmed(values: Readonly<Record<string, unknown>>, fields: readonly string[]): string | undefined {
  for (const field of fields) {
    const value = optionalTrimmed(values[field]);
    if (value) return value;
  }
  return undefined;
}

function resolveOrganizationUnitId(values: Readonly<Record<string, unknown>>, context: ComplaintCreateContext): string {
  const selected = firstTrimmed(values, ["organizationUnitId", "orgUnitId"]);
  if (selected) return selected;
  const explicit = optionalTrimmed(context.organizationUnitId);
  if (explicit) return explicit;
  const scoped = context.organizationUnitIds?.map((item) => optionalTrimmed(item)).filter((item): item is string => Boolean(item)) ?? [];
  if (scoped.length === 1 && scoped[0]) return scoped[0];
  throw new Error("organizationUnitId is required from organization scope or selected organization unit.");
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function optionalGuid(value: unknown): string | undefined {
  const trimmed = optionalTrimmed(value);
  if (!trimmed) return undefined;
  return GUID_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function buildComplaintCreatePayload(values: Readonly<Record<string, unknown>>, context: ComplaintCreateContext = {}): ComplaintCreatePayload {
  const organizationUnitId = resolveOrganizationUnitId(values, context);
  const complaintTypeId = optionalGuid(firstTrimmed(values, ["complaintTypeId", "issueTypeId", "typeId"]));
  const subCategoryId = optionalGuid(firstTrimmed(values, ["subCategoryId", "subcategoryId"]));
  const payload: ComplaintCreatePayload = {
    title: maxLength(requiredTrimmed(values, "title"), "title", 250),
    description: maxLength(requiredTrimmed(values, "description"), "description", 5000),
    sourceId: requiredTrimmed(values, "sourceId"),
    ...(complaintTypeId ? { complaintTypeId } : {}),
    categoryId: requiredTrimmed(values, "categoryId"),
    ...(subCategoryId ? { subCategoryId } : {}),
    severityId: requiredTrimmed(values, "severityId"),
    priorityId: requiredTrimmed(values, "priorityId"),
    organizationUnitId,
    plantId: optionalTrimmed(values.plantId),
    departmentId: optionalTrimmed(values.departmentId),
    externalPartyId: optionalTrimmed(values.externalPartyId),
    partNumber: optionalTrimmed(values.partNumber),
    batchNumber: optionalTrimmed(values.batchNumber),
    lotNumber: optionalTrimmed(values.lotNumber),
    tagIds: optionalStringArray(values.tagIds),
    fieldValues: optionalStringRecord(values.fieldValues),
    isConfidential: optionalBoolean(values.isConfidential),
    isAnonymous: optionalBoolean(values.isAnonymous),
    customerId: optionalTrimmed(values.customerId),
    supplierId: optionalTrimmed(values.supplierId),
    internalPartyId: optionalTrimmed(values.internalPartyId),
    productId: optionalTrimmed(values.productId),
    processId: optionalTrimmed(values.processId),
    productionLineId: optionalTrimmed(values.productionLineId),
    machineId: optionalTrimmed(values.machineId),
    ownerUserId: optionalTrimmed(values.ownerUserId),
    functionalExpertId: optionalTrimmed(values.functionalExpertId),
    occurrenceAt: optionalDate(values.occurrenceAt),
    detectionAt: optionalDate(values.detectionAt),
    reportedAt: optionalDate(values.reportedAt),
    reporterName: optionalTrimmed(values.reporterName),
    reporterEmail: optionalTrimmed(values.reporterEmail),
    externalReferenceNumber: firstTrimmed(values, ["externalReferenceNumber", "customerReference"]),
    serialNumber: optionalTrimmed(values.serialNumber),
    quantityAffected: optionalNumber(values.quantityAffected),
    confidentialityLevelId: optionalTrimmed(values.confidentialityLevelId),
    clientMutationId: optionalTrimmed(values.clientMutationId),
    correlationId: optionalTrimmed(values.correlationId),
  };
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)) as ComplaintCreatePayload;
}

export const buildComplaintCreateRequest = buildComplaintCreatePayload;

export function buildRcaCreatePayload(values: Readonly<Record<string, unknown>>): RcaCreatePayload {
  return {
    complaintId: requiredTrimmed(values, "complaintId"),
    rcaMethodId: requiredTrimmed(values, "rcaMethodId"),
    rootCauseCategoryId: requiredTrimmed(values, "rootCauseCategoryId"),
    problemStatement: minLength(requiredTrimmed(values, "problemStatement"), "problemStatement", 5),
    ...(optionalTrimmed(values.investigationSummary) ? { investigationSummary: optionalTrimmed(values.investigationSummary) } : {}),
    ...(optionalTrimmed(values.rootCause) ? { rootCause: optionalTrimmed(values.rootCause) } : {}),
    ownerUserId: requiredTrimmed(values, "ownerUserId"),
    targetDate: requiredTrimmed(values, "targetDate"),
    statusId: requiredTrimmed(values, "statusId"),
  };
}

export function buildCapaCreatePayload(
  values: Readonly<
    Record<string, unknown>
  >,
): CapaCreatePayload {
  const dueDate =
    optionalDate(
      values.dueDate,
    );

  if (!dueDate) {
    throw new Error(
      "dueDate is required and must be a valid date.",
    );
  }

  const effectivenessRequired =
    optionalBoolean(
      values.effectivenessRequired,
    ) ?? false;

  const effectivenessCheckDate =
    optionalDate(
      values.effectivenessCheckDate,
    );

  if (
    effectivenessRequired &&
    !effectivenessCheckDate
  ) {
    throw new Error(
      "effectivenessCheckDate is required when effectivenessRequired is Yes.",
    );
  }

  const dueDateValue =
    new Date(dueDate);

  if (
    Number.isNaN(
      dueDateValue.getTime(),
    )
  ) {
    throw new Error(
      "dueDate must be a valid date.",
    );
  }

  if (effectivenessCheckDate) {
    const effectivenessDateValue =
      new Date(
        effectivenessCheckDate,
      );

    if (
      Number.isNaN(
        effectivenessDateValue
          .getTime(),
      )
    ) {
      throw new Error(
        "effectivenessCheckDate must be a valid date.",
      );
    }

    if (
      effectivenessDateValue <=
      dueDateValue
    ) {
      throw new Error(
        "effectivenessCheckDate must be after dueDate.",
      );
    }
  }

  return {
    complaintId:
      requiredTrimmed(
        values,
        "complaintId",
      ),

    actionTypeId:
      requiredTrimmed(
        values,
        "actionTypeId",
      ),

    actionDescription:
      minLength(
        requiredTrimmed(
          values,
          "actionDescription",
        ),
        "actionDescription",
        5,
      ),

    ownerUserId:
      requiredTrimmed(
        values,
        "ownerUserId",
      ),

    dueDate,

    statusId:
      requiredTrimmed(
        values,
        "statusId",
      ),

    effectivenessRequired,

    ...(effectivenessCheckDate
      ? {
          effectivenessCheckDate,
        }
      : {}),
  };
}

export function buildVerificationCreatePayload(values: Readonly<Record<string, unknown>>): VerificationCreatePayload {
  return {
    complaintId: requiredTrimmed(values, "complaintId"),
    verificationRuleId: requiredTrimmed(values, "verificationRuleId"),
    verifierUserId: requiredTrimmed(values, "verifierUserId"),
    pendingStatusId: requiredTrimmed(values, "pendingStatusId"),
  };
}


export function buildEvidenceUploadMetadata(
  input: Readonly<{
    description?: string | undefined;
    confidentiality?: string | undefined;
    file: File;
  }>,
): EvidenceUploadMetadata {
  const confidentiality =
    optionalTrimmed(input.confidentiality);

  if (!confidentiality) {
    throw new Error("confidentiality is required.");
  }

  return {
    description: optionalTrimmed(input.description),
    confidentiality,
    fileName: input.file.name,
    fileType: input.file.type,
    sizeBytes: input.file.size,
  };
}
